import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

/**
 * ProductV3 is the ONLY product identity these surfaces speak.
 *
 * `im_inventory_snapshots`, `im_inventory_policies` and `im_inventory_lots` were
 * all re-keyed to `product_v3_key`, and the legacy `product_id` is NULL on every
 * row of a post-cutover database. Carrying both identities bought nothing and
 * cost a lot: matching either one needs an `OR` across two columns, which demotes
 * the policy join from an exact index probe to a scan (measured 38.3s vs 312ms on
 * a 552k-row list page, and a full 720k-row seq scan on the detail page), and it
 * admits a v2-only and a v3-only policy row for the same pair, double-counting
 * on-hand. Neither risk is worth a v2 fallback that resolves nothing.
 *
 * `productSku` stays nullable because `display_sku` can be NULL; `key` is the
 * fallback and is never null.
 */
export const InventorySnapshotRowSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  warehouseType: z.string().nullable(),
  productV3Key: z.string(),
  productSku: z.string().nullable(),
  /** `display_description`, which is NULL for most of the catalog. */
  productName: z.string().nullable(),
  snapshotDate: z.string(),
  onHandQuantity: z.number(),
  reservedQuantity: z.number(),
  availableQuantity: z.number(),
  hasLots: z.boolean(),
  lastSyncedAt: z.string(),
  // Inventory-policy lookup (per product × warehouse). Nullable when the
  // SKU/warehouse pair has no policy row in `im_inventory_policies` yet.
  popularityCode: z.string().nullable(),
  stockStatus: z.string().nullable(),
});

export type InventorySnapshotRow = z.infer<typeof InventorySnapshotRowSchema>;

// ─── List ───────────────────────────────────────────────────────────────────

export const ListInventorySnapshotsInputSchema = z.object({
  divisionId: z.string().uuid().optional(),
  warehouseId: z.string().uuid().optional(),
  search: z.string().optional(),
  snapshotDate: z.string().optional(),
  hideZeroQuantity: z.boolean().optional().default(false),
  activeProductsOnly: z.boolean().optional(),
  warehouseNames: z.array(z.string()).optional(),
  hasLots: z.boolean().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListInventorySnapshotsResponseSchema = z.object({
  rows: z.array(InventorySnapshotRowSchema),
  totalRows: z.number(),
  effectiveSnapshotDate: z.string().nullable(),
});

export type ListInventorySnapshotsInput = z.infer<
  typeof ListInventorySnapshotsInputSchema
>;
export type ListInventorySnapshotsResponse = z.infer<
  typeof ListInventorySnapshotsResponseSchema
>;

// ─── Product detail ─────────────────────────────────────────────────────────

/** Addressed by the ProductV3 key (the SKU), which is the only identity here. */
export const GetProductInventoryDetailInputSchema = z.object({
  productV3Key: z.string().min(1),
  snapshotDate: z.string().optional(),
});

export const ProductWarehouseSnapshotSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  warehouseType: z.string().nullable(),
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string().nullable(),
  onHandQuantity: z.number(),
  reservedQuantity: z.number(),
  availableQuantity: z.number(),
  lastSyncedAt: z.string(),
  popularityCode: z.string().nullable(),
  stockStatus: z.string().nullable(),
  // Buyer's per-item source decision (INITINFC giparent1) for this SKU at this
  // warehouse: 'transfer' (from sourceWarehouseName), 'buy', or null (unset).
  sourceType: z.enum(["transfer", "buy"]).nullable(),
  sourceWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  // Forecasting-engine outputs on this SKU×warehouse policy (null until the
  // engine has computed them). Demand-driven buffer + reorder level, the
  // service level they target, and when they were last computed.
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  targetAvailability: z.number().nullable(),
  forecastCalculatedAt: z.string().nullable(),
});

export const ProductInventoryLotSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  unitNumber: z.string(),
  quantity: z.number(),
});

export const GetProductInventoryDetailResponseSchema = z.object({
  productV3Key: z.string(),
  productSku: z.string().nullable(),
  /**
   * `display_description`. NULL for most of the catalog (4.1M of 4.5M rows on a
   * prod-derived clone), and deliberately NOT defaulted to the key: a name that
   * echoes the SKU makes the detail page print the same string twice and turns
   * the list's Description column into a copy of its SKU column.
   */
  productName: z.string().nullable(),
  /**
   * Whether the product resolved in the catalog at all. False means the SKU has
   * snapshots but no `im_products_v3` row, which is a catalog gap worth showing
   * rather than a 404.
   */
  isKnownProduct: z.boolean(),
  isActive: z.boolean(),
  effectiveSnapshotDate: z.string().nullable(),
  totalOnHand: z.number(),
  totalReserved: z.number(),
  totalAvailable: z.number(),
  warehouses: z.array(ProductWarehouseSnapshotSchema),
  lots: z.array(ProductInventoryLotSchema),
});

export type GetProductInventoryDetailInput = z.infer<
  typeof GetProductInventoryDetailInputSchema
>;
export type GetProductInventoryDetailResponse = z.infer<
  typeof GetProductInventoryDetailResponseSchema
>;
export type ProductWarehouseSnapshot = z.infer<
  typeof ProductWarehouseSnapshotSchema
>;
export type ProductInventoryLot = z.infer<typeof ProductInventoryLotSchema>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const inventorySnapshotsContracts = {
  list: oc
    .input(ListInventorySnapshotsInputSchema)
    .output(ListInventorySnapshotsResponseSchema)
    .route({
      method: "GET",
      summary: "List on-hand inventory by warehouse and product",
      description:
        "Paged on-hand, reserved and available quantities per warehouse and product for a snapshot date (latest by default). Filter by division, warehouse, search, lots, zero quantity.",
    }),
  getByProduct: oc
    .input(GetProductInventoryDetailInputSchema)
    .output(GetProductInventoryDetailResponseSchema)
    .route({
      method: "GET",
      summary: "Get a product's inventory across warehouses",
      description:
        "For one SKU, returns on-hand, reserved and available totals, per-warehouse quantities with policy, source and safety-stock/reorder levels, plus lot units.",
    }),
} as const;
